import React, { useState } from 'react';
import { Form, Input, Button, Checkbox, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { logger } from '../utils/logger';
import { storageHelper } from '../utils/storageHelper';
import './Login.css';

interface LoginFormValues {
  username: string;
  password: string;
  remember: boolean;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values: LoginFormValues) => {
    setLoading(true);
    try {
      // 먼저 username으로 사용자 정보 조회
      const { data: userCheck, error: userCheckError } = await supabase
        .from('users')
        .select('auth_id, is_active, has_admin_role, is_admin')
        .eq('username', values.username)
        .single();

      if (userCheckError || !userCheck) {
        message.error('존재하지 않는 사용자입니다.');
        setLoading(false);
        return;
      }

      if (!userCheck.is_active) {
        message.error('비활성화된 계정입니다.');
        setLoading(false);
        return;
      }

      if (!userCheck.has_admin_role && !userCheck.is_admin) {
        message.error('관리자 권한이 없습니다.');
        setLoading(false);
        return;
      }

      // Supabase 인증
      const { data, error } = await supabase.auth.signInWithPassword({
        email: `${values.username}@tbm.app`,
        password: values.password,
      });

      if (error) {
        message.error('아이디 또는 비밀번호를 확인하세요.');
        setLoading(false);
        return;
      }

      if (data.user) {
        // 사용자 정보 확인
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('*, company:companies(name), branch:branches!users_branch_id_fkey(branch_name)')
          .eq('auth_id', data.user.id)
          .single();

        if (userError || !userData) {
          message.error('사용자 정보를 찾을 수 없습니다.');
          await supabase.auth.signOut();
          return;
        }

        // 관리자 권한 확인
        if (!userData.has_admin_role && !userData.is_admin) {
          message.error('관리자 권한이 없습니다.');
          await supabase.auth.signOut();
          return;
        }

        // 활성 상태 확인
        if (!userData.is_active) {
          message.error('비활성화된 계정입니다.');
          await supabase.auth.signOut();
          return;
        }

        // 로그인 성공
        message.success(`${userData.name}님 환영합니다!`);
        
        // 자동 로그인 설정 저장
        storageHelper.setRememberMe(values.remember);
        
        // 보안 강화된 스토리지에 저장
        storageHelper.setAuthenticated(true);
        storageHelper.saveUserData(userData);
        
        // 최초 로그인 시 비밀번호 변경 페이지로
        if (userData.is_first_login) {
          // TODO: 비밀번호 변경 페이지로 이동
          navigate('/change-password');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      logger.error('Login error', error, { username: values.username });
      message.error('로그인 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-left">
        <div className="brand-content">
          <h1 className="brand-logo">CLEAN ZONE</h1>
          <h2 className="brand-title">안전하고 깨끗한 작업 현장을 위한</h2>
          <h2 className="brand-subtitle">크린존 관리시스템</h2>
          <div className="brand-illustration">
            {/* 일러스트레이션 추가 예정 */}
          </div>
        </div>
      </div>
      
      <div className="login-right">
        <div className="login-form-container">
          <h2 className="login-title">관리자 로그인</h2>
          
          <Form
            name="login"
            className="login-form"
            initialValues={{ remember: true }}
            onFinish={onFinish}
            size="large"
          >
            <Form.Item
              name="username"
              rules={[{ required: true, message: '아이디를 입력해주세요!' }]}
            >
              <Input 
                prefix={<UserOutlined className="site-form-item-icon" />} 
                placeholder="아이디" 
              />
            </Form.Item>
            
            <Form.Item
              name="password"
              rules={[{ required: true, message: '비밀번호를 입력해주세요!' }]}
            >
              <Input.Password
                prefix={<LockOutlined className="site-form-item-icon" />}
                placeholder="비밀번호"
              />
            </Form.Item>
            
            <Form.Item>
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox>자동 로그인</Checkbox>
              </Form.Item>
            </Form.Item>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                className="login-form-button"
                loading={loading}
                block
              >
                로그인
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Login;